import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  Alert,
  CircularProgress,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip
} from '@mui/material';
import {
  AccountCircle,
  HowToReg,
  Business,
  Phone,
  Security,
  CheckCircle,
  ArrowBack
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';

// Account verification steps
enum VerificationStep {
  ACCOUNT_DETAILS = 'account_details',
  OTP_VERIFICATION = 'otp_verification',
  SUCCESS = 'success'
}

const AccountVerification: React.FC = () => {
  const navigate = useNavigate();
  
  // Form state
  const [accountNumber, setAccountNumber] = useState('');
  const [nationalId, setNationalId] = useState('');
  const [companyRegistration, setCompanyRegistration] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [maskedPhone, setMaskedPhone] = useState('');
  
  // UI state
  const [currentStep, setCurrentStep] = useState<VerificationStep>(VerificationStep.ACCOUNT_DETAILS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState('');
  const [success, setSuccess] = useState('');
  const [retryAllowed, setRetryAllowed] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [canResend, setCanResend] = useState(false);

  const validateAccountDetails = () => {
    // Trim string inputs for validation
    const trimmedAccountNumber = accountNumber?.trim() || '';
    const trimmedCompanyRegistration = companyRegistration?.trim() || '';
    const trimmedPhoneNumber = phoneNumber?.trim() || '';
    
    if (!trimmedAccountNumber) {
      setError('Please enter your account number');
      return false;
    }
    if (!trimmedCompanyRegistration) {
      setError('Please enter your Company Registration number');
      return false;
    }
    if (!trimmedPhoneNumber) {
      setError('Please enter your phone number');
      return false;
    }
    return true;
  };

  const validateOtp = () => {
    if (!otp || otp.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return false;
    }
    return true;
  };

  const handleAccountVerification = async () => {
    if (!validateAccountDetails()) return;
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Get username from user_info in localStorage
      const userInfo = localStorage.getItem('user_info');
      const username = userInfo ? JSON.parse(userInfo).username : '';
      
      const verificationData = {
        accountNumber: accountNumber?.trim(),
        companyRegistration: companyRegistration?.trim() || null,
        phone: phoneNumber?.trim(),
        username: username?.trim()
      };
      
      
      const response = await api.post('/api/auth/account/verify/start', verificationData);
      const data = response.data;
      
      if (response.status === 200 && data?.transactionId) {
        setTransactionId(data.transactionId);
        setMaskedPhone(data.maskedPhone || 'your phone');
        setSuccess(`Verification code sent to ****${data.maskedPhone}. Please enter the OTP.`);
        setCurrentStep(VerificationStep.OTP_VERIFICATION);
      } else {
        setError(data?.message || 'Verification failed. Please check your details and try again.');
      }
    } catch (err: any) {
      const response = err.response?.data;
      const msg = response?.message || 'Verification failed. Please check your details and try again.';
      const details = response?.details || '';
      const retry = response?.retryAllowed || false;
      const attempts = response?.remainingAttempts || 0;
      
      setError(msg);
      setErrorDetails(details);
      setRetryAllowed(retry);
      setRemainingAttempts(attempts);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setError('');
    setErrorDetails('');
    setRetryAllowed(false);
    setRemainingAttempts(0);
    setOtp('');
    // Reset to the appropriate step based on current step
    if (currentStep === VerificationStep.OTP_VERIFICATION) {
      setCurrentStep(VerificationStep.ACCOUNT_DETAILS);
    }
  };

  const handleResendOtp = async () => {
    if (!transactionId) {
      setError('No verification session found. Please restart the process.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/api/auth/account/verify/resend-otp', {
        transactionId
      });

      if (response.status === 200) {
        setSuccess(response.data.message || 'New verification code sent successfully!');
        setCanResend(false);
        setResendCooldown(60); // 60 seconds cooldown
      } else {
        setError(response.data.message || 'Failed to resend verification code');
      }
    } catch (err: any) {
      const response = err.response?.data;
      const msg = response?.message || 'Failed to resend verification code. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  // Countdown timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [resendCooldown]);

  // Enable resend after OTP is sent initially
  useEffect(() => {
    if (currentStep === VerificationStep.OTP_VERIFICATION && transactionId) {
      setCanResend(true);
      setResendCooldown(0);
    }
  }, [currentStep, transactionId]);

  const handleOtpVerification = async () => {
    if (!validateOtp()) return;
    
    setLoading(true);
    setError('');
    
    try {
      const otpData = {
        transactionId,
        otp
      };
      
      const response = await api.post('/api/auth/account/verify/otp', otpData);
      const data = response.data;
      
      if (response.status === 200 && data?.status === 'verified') {
        setSuccess(data.message || 'Account verified successfully! Redirecting to login...');
        setCurrentStep(VerificationStep.SUCCESS);
        
        // Redirect to login page after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setError(data?.message || 'Invalid verification code');
      }
    } catch (err: any) {
      const response = err.response?.data;
      const msg = response?.message || 'Verification failed. Please try again.';
      const details = response?.details || '';
      const retry = response?.retryAllowed || false;
      const attempts = response?.remainingAttempts || 0;
      
      setError(msg);
      setErrorDetails(details);
      setRetryAllowed(retry);
      setRemainingAttempts(attempts);
    } finally {
      setLoading(false);
    }
  };

  const handleSkipVerification = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Call backend to mark user as skipped verification
      const response = await api.post('/api/auth/account/skip-verification', {
        username: localStorage.getItem('user_info') ? JSON.parse(localStorage.getItem('user_info')!).username : null
      });
      
      if (response.status === 200) {
        setSuccess('Verification skipped. Redirecting to profile...');
        setCurrentStep(VerificationStep.SUCCESS);
        
        // Redirect to profile page after 2 seconds
        setTimeout(() => {
          navigate('/profile');
        }, 2000);
      } else {
        setError('Failed to skip verification. Please try again.');
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to skip verification. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      label: 'Account Details',
      description: 'Enter your SMEDCO account information'
    },
    {
      label: 'Verify OTP',
      description: 'Enter the verification code sent to your phone'
    },
    {
      label: 'Success',
      description: 'Account verified successfully'
    }
  ];

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4
      }}
    >
      <Container maxWidth="md">
        <Paper 
          elevation={24} 
          sx={{ 
            borderRadius: 4,
            overflow: 'hidden',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)'
          }}
        >
          {/* Header */}
          <Box sx={{ 
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            color: 'white',
            p: 4,
            textAlign: 'center'
          }}>
            <Security sx={{ fontSize: 48, mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              Verify Your Account
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Add your SMEDCO account details to get full access to all features
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ p: 4 }}>
            {/* Progress Stepper */}
            <Stepper activeStep={Object.values(VerificationStep).indexOf(currentStep)} orientation="vertical" sx={{ mb: 4 }}>
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    StepIconComponent={() => (
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: Object.values(VerificationStep).indexOf(currentStep) >= index ? 'primary.main' : 'grey.300',
                          color: 'white'
                        }}
                      >
                        {index + 1}
                      </Box>
                    )}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {step.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step.description}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    {/* Step 1: Account Details */}
                    {currentStep === VerificationStep.ACCOUNT_DETAILS && (
                      <Box sx={{ mt: 2 }}>
                        <Card sx={{ p: 3, mb: 3, border: '1px solid #e0e0e0' }}>
                          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                            Enter Your SMEDCO Account Information
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            We'll verify your account details with our records to give you full access.
                          </Typography>
                          
                          <Grid container spacing={3}>
                            <Grid item xs={12}>
                              <TextField 
                                label="SMEDCO Account Number" 
                                fullWidth 
                                value={accountNumber} 
                                onChange={(e) => setAccountNumber(e.target.value)}
                                required
                                variant="outlined"
                                size="medium"
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <AccountCircle sx={{ color: 'primary.main' }} />
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '&:hover fieldset': {
                                      borderColor: 'primary.main',
                                    },
                                  },
                                }}
                              />
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <TextField
                                label="Company Registration Number"
                                value={companyRegistration}
                                onChange={(e) => setCompanyRegistration(e.target.value)}
                                fullWidth
                                required
                                variant="outlined"
                                size="medium"
                                helperText="Your company registration number (required for verification)"
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Business sx={{ color: 'primary.main' }} />
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '&:hover fieldset': {
                                      borderColor: 'primary.main',
                                    },
                                  },
                                }}
                              />
                            </Grid>
                            
                            <Grid item xs={12}>
                              <TextField
                                label="Phone Number"
                                value={phoneNumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                fullWidth
                                variant="outlined"
                                size="medium"
                                required
                                helperText="Your registered phone number with SMEDCO"
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Phone sx={{ color: 'primary.main' }} />
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '&:hover fieldset': {
                                      borderColor: 'primary.main',
                                    },
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Card>
                        
                        <Button
                          onClick={handleAccountVerification}
                          variant="contained"
                          size="large"
                          fullWidth
                          disabled={loading}
                          sx={{
                            py: 1.5,
                            borderRadius: 2,
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                              transform: 'translateY(-1px)',
                            },
                            transition: 'all 0.3s ease',
                            mb: 2
                          }}
                        >
                          {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify Account'}
                        </Button>

                        <Button
                          onClick={handleSkipVerification}
                          variant="outlined"
                          size="large"
                          fullWidth
                          disabled={loading}
                          sx={{
                            py: 1.5,
                            borderRadius: 2,
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderColor: 'text.secondary',
                            color: 'text.secondary',
                            '&:hover': {
                              borderColor: 'text.primary',
                              backgroundColor: 'action.hover',
                              transform: 'translateY(-1px)',
                            },
                            transition: 'all 0.3s ease',
                          }}
                        >
                          Skip Verification for Now
                        </Button>
                      </Box>
                    )}

                    {/* Step 2: OTP Verification */}
                    {currentStep === VerificationStep.OTP_VERIFICATION && (
                      <Box sx={{ mt: 2 }}>
                        <Card sx={{ p: 3, mb: 3, border: '1px solid #e0e0e0' }}>
                          <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                            Enter Verification Code
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            We've sent a 6-digit code to ****{maskedPhone}. Please enter it below.
                          </Typography>
                          
                          <TextField
                            label="Verification Code"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            fullWidth
                            required
                            variant="outlined"
                            size="large"
                            placeholder="000000"
                            inputProps={{
                              maxLength: 6,
                              style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5em' }
                            }}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <Security sx={{ color: 'primary.main' }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                '&:hover fieldset': {
                                  borderColor: 'primary.main',
                                },
                              },
                            }}
                          />
                        </Card>
                        
                        <Button
                          onClick={handleOtpVerification}
                          variant="contained"
                          size="large"
                          fullWidth
                          disabled={loading}
                          sx={{
                            py: 1.5,
                            borderRadius: 2,
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                              transform: 'translateY(-1px)',
                            },
                            transition: 'all 0.3s ease',
                          }}
                        >
                          {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify Code'}
                        </Button>

                        {/* Resend OTP Section */}
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Didn't receive the code?
                          </Typography>
                          
                          {canResend ? (
                            <Button
                              onClick={handleResendOtp}
                              variant="outlined"
                              size="medium"
                              disabled={loading}
                              sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                borderColor: 'primary.main',
                                color: 'primary.main',
                                '&:hover': {
                                  borderColor: 'primary.dark',
                                  backgroundColor: 'primary.50',
                                },
                              }}
                            >
                              {loading ? <CircularProgress size={20} color="inherit" /> : 'Resend Code'}
                            </Button>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                              <Typography variant="body2" color="text.secondary">
                                Resend available in:
                              </Typography>
                              <Chip
                                label={`${resendCooldown}s`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Step 3: Success */}
                    {currentStep === VerificationStep.SUCCESS && (
                      <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Card sx={{ p: 4, mb: 3, border: '1px solid #4caf50' }}>
                          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                          <Typography variant="h5" gutterBottom sx={{ color: 'success.main', fontWeight: 600 }}>
                            {success.includes('skipped') ? 'Verification Skipped' : 'Account Verified Successfully!'}
                          </Typography>
                          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            {success.includes('skipped') 
                              ? 'You can verify your account later from your profile. Redirecting to login...'
                              : 'Your SMEDCO account has been verified. Redirecting to login...'
                            }
                          </Typography>
                          <Chip 
                            label="Redirecting to Login..." 
                            color="success" 
                            variant="outlined"
                            sx={{ fontWeight: 600 }}
                          />
                        </Card>
                      </Box>
                    )}
                  </StepContent>
                </Step>
              ))}
            </Stepper>

            {/* Error/Success Messages */}
            {error && (
              <Alert 
                severity="error" 
                sx={{ mb: 2, borderRadius: 2 }}
                action={
                  retryAllowed && (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={handleRetry}
                      sx={{ 
                        fontWeight: 'bold',
                        textTransform: 'none'
                      }}
                    >
                      Try Again
                    </Button>
                  )
                }
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: errorDetails ? 1 : 0 }}>
                    {error}
                  </Typography>
                  {errorDetails && (
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {errorDetails}
                    </Typography>
                  )}
                  {remainingAttempts > 0 && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                      {remainingAttempts} attempt(s) remaining
                    </Typography>
                  )}
                </Box>
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                {success}
              </Alert>
            )}

            {/* Back Button */}
            {(currentStep === VerificationStep.OTP_VERIFICATION) && (
              <Button
                onClick={() => setCurrentStep(VerificationStep.ACCOUNT_DETAILS)}
                variant="outlined"
                size="large"
                fullWidth
                startIcon={<ArrowBack />}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    backgroundColor: 'primary.50',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Back to Account Details
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default AccountVerification;
